import { Button, Grid, IconButton, Paper, TextField, InputAdornment, Autocomplete, CircularProgress, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setStoreData, getAllAssetNames, addInventoryItems, getInventoryItemById, updateInventoryItem, deleteInventoryItem } from '../../Redux/actions';
import dayjs from 'dayjs';
import AlertDialog from '../Common/AlertDialog';
import moment from 'moment';
import ConfirmDialog from '../Common/ConfirmDialog';
import { isValidDate } from '../../utils/Common';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CloseIcon from '@mui/icons-material/Close';
import Loader from '../Common/Loader';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

export default function ManageInventoryItem() {

    const location = useLocation();

    let { id, tabId } = useParams()
    let navigate = useNavigate()
    const dispatch = useDispatch();

    const [assetEntries, setAssetEntries] = useState([
        {
            itemId: '',
            itemName: '',
            serialNumber: '',
            cost: '',
            vendorName: '',
            billNumber: '',
            particulars: '',
            coordinates: '',
            purchaseDate: '',
            endOfWarrantyDate: '',
        }
    ]);
    const [alertMessage, setAlertMessage] = useState();
    const [itemNotFound, setItemNotFound] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const error = {};
    const [errors, setErrors] = useState(error);
    const [disableButton, setDisableButton] = useState(false);
    const [showLoader, setShowLoader] = useState(id ? true : false)
    const [deletedAssets, setDeletedAssets] = useState([]);
    const [clickSubmitButton, setClickSubmitButton] = useState(false);
    const [geoError, setGeoError] = useState('');
    const [assetNames, setAssetNames] = useState([]);
    const inventoryQueryParams = localStorage.getItem("inventoryQueryParams");

    useEffect(() => {
        dispatch(getAllAssetNames()).then((res) => {
            if (res && res.status === 200) {
                setAssetNames(
                    res?.data?.assetNames?.map(item => ({
                        id: item?.id,
                        asset_name: item?.asset_name
                    }))
                );
            }
        })
    }, [])

    useEffect(() => {
        if (id) {
            dispatch(getInventoryItemById(id)).then((resp) => {
                const response = resp?.data?.inventoryItem
                if (!response) {
                    setItemNotFound(true);
                    setShowLoader(false)
                    return;
                }

                const { item_name, serial_number, asset_cost, bill_number, vendor_name, particulars, geo_proof_lat, geo_proof_lon, purchase_date, warranty_end_date } = response

                const existData = {
                    itemName: item_name,
                    serialNumber: serial_number,
                    cost: asset_cost,
                    vendorName: vendor_name,
                    billNumber: bill_number,
                    particulars: particulars,
                    coordinates: (geo_proof_lat && geo_proof_lon) ? `${geo_proof_lat}, ${geo_proof_lon}` : '',
                    purchaseDate: purchase_date ? moment(purchase_date).toISOString() : '',
                    endOfWarrantyDate: warranty_end_date ? moment(warranty_end_date).toISOString() : '',
                }
                
                setAssetEntries([existData]);
                setShowLoader(false);
            })
        }
    }, []);

    const handleAssetChange = (index, field, value) => {

        if (field === 'cost') {
            const isValid = /^[0-9]*\.?[0-9]*$/.test(value);
            if (!isValid) {
                return;
            }
        }

        const updatedAssets = [...assetEntries];
        updatedAssets[index][field] = value;
        setAssetEntries(updatedAssets);

        const errorKey = `asset_${index}_${field}`;
        if (errors[errorKey]) {
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[errorKey];
                return newErrors;
            });
        }

        if (disableButton) {
            setDisableButton(false)
        }
    };

    const getCurrentLocation = (index) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const coords = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
                    handleAssetChange(index, 'coordinates', coords);
                },
                (error) => {
                    console.error("Error fetching location: ", error);
                    setGeoError(error.message || "Failed to fetch location");
                }
            );
        } else {
            console.error("Geolocation not supported by this browser.");
            setGeoError("Geolocation is not supported by this browser.");
        }
    };

    const updateResponse = (resp, message) => {
        const res = resp && resp.data;
        let msg = res.message;
        let type = "fail";
        if (res && res.success) {
            msg = message;
            type = "success";
        }
        dispatch(setStoreData('alertMessage', {
            msg, type,
            handleClose: () => handleCloseDialog(),
        }))
    };

    const handleCloseDialog = () => {
        dispatch(setStoreData('alertMessage', null));
 
        if (inventoryQueryParams) {
            navigate(`/inventory-management/${tabId}?${inventoryQueryParams.toString()}`)
        } else {
            navigate(`/inventory-management/${tabId}`)
        }
    };

    const getComparableDate = (date) => {
        if (!date) return null;
        return dayjs.isDayjs(date) ? date.toDate() : new Date(date);
    };

    const validateData = () => {
        let hasErrors = false;
        const err = {};

        assetEntries.forEach((asset, index) => {
            const fieldPrefix = `asset_${index}_`;

            const purchaseDate = getComparableDate(asset?.purchaseDate);

            if (!asset.itemName) {
                hasErrors = true;
                err[`${fieldPrefix}itemName`] = 'Item name is required';
            }

            if (!asset.serialNumber || asset.serialNumber.trim() === '') {
                hasErrors = true;
                err[`${fieldPrefix}serialNumber`] = 'Serial number is required';
            }

            if (!asset.cost || asset.cost.trim() === '') {
                hasErrors = true;
                err[`${fieldPrefix}cost`] = 'Asset cost is required';
            }

            if (!asset.particulars || asset.particulars.trim() === '') {
                hasErrors = true;
                err[`${fieldPrefix}particulars`] = 'Particulars are required';
            }

            if (!asset.purchaseDate) {
                hasErrors = true;
                err[`${fieldPrefix}purchaseDate`] = 'Purchase date is required';
            }
            if (asset.purchaseDate > new Date()) {
                hasErrors = true;
                err[`${fieldPrefix}purchaseDate`] = 'Future date cannot be selected';
            }
            if (asset.purchaseDate && !isValidDate(asset.purchaseDate)) {
                hasErrors = true;
                err[`${fieldPrefix}purchaseDate`] = 'Please enter a valid date in the format DD/MM/YYYY';
            }

            if (asset.coordinates) {
                const coordinatePattern = /^-?\d{1,2}(\.\d+)?,\s?-?\d{1,3}(\.\d+)?$/;
                if (!coordinatePattern.test(asset.coordinates)) {
                    hasErrors = true;
                    err[`${fieldPrefix}coordinates`] = 'Coordinates are invalid';
                }
            }

            const endOfWarrantyDate = getComparableDate(asset?.endOfWarrantyDate)
            if (asset.endOfWarrantyDate && !isValidDate(asset.endOfWarrantyDate)) {
                hasErrors = true;
                err[`${fieldPrefix}endOfWarrantyDate`] = 'Please enter a valid date in the format DD/MM/YYYY';
            }
            if ((asset.endOfWarrantyDate && asset.purchaseDate) && (endOfWarrantyDate < purchaseDate)) {
                hasErrors = true;
                err[`${fieldPrefix}endOfWarrantyDate`] = 'End of warranty date cannot be less than purchase date';
            }
        });

        setErrors(err);
        return !hasErrors;
    };

    const handleSubmit = async () => {
        setDisableButton(true);
        const checkForm = validateData();

        if (checkForm) {
            setClickSubmitButton(true) 
            const payload = [];

            assetEntries.forEach((entry, index) => {
                let latitude = '';
                let longitude = '';
                if (entry?.coordinates) {
                    [latitude, longitude] = entry.coordinates.split(',').map(coord => coord.trim());
                }

                const assetData = {
                    itemId: entry?.itemId || null,
                    itemName: entry.itemName,
                    serialNumber: entry.serialNumber,
                    particulars: entry.particulars,
                    purchaseDate: entry.purchaseDate,
                    latitude: latitude || '',
                    longitude: longitude || '',
                    cost: entry.cost,
                    billNumber: entry.billNumber,
                    vendorName: entry.vendorName,
                    endOfWarrantyDate: entry.endOfWarrantyDate,
                };

                payload.push(assetData);

            });

            if (id) {
                dispatch(updateInventoryItem(payload, id)).then((resp) => {
                    setDisableButton(false)
                    setClickSubmitButton(false)
                    if (!resp) {
                        return;
                    } else {
                        updateResponse(resp, resp?.data?.message);
                    }
                });
            } else {
                dispatch(addInventoryItems(payload)).then((resp) => {
                    setDisableButton(false)
                    setClickSubmitButton(false)
                    if (!resp) {
                        return;
                    } else {
                        updateResponse(resp, resp?.data?.message);
                    }
                });
            }
        }
    };


    const handleRemoveAssetEntry = (indexToRemove) => {
        setAssetEntries((prevEntries) => {
            const assetToRemove = prevEntries[indexToRemove];

            const itemId = assetToRemove?.itemId;
            if (itemId) {
                setDeletedAssets((prev) => [...prev, itemId]);
            }

            return prevEntries.filter((_, index) => index !== indexToRemove);
        });

        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            Object.keys(updatedErrors).forEach((key) => {
                if (key.startsWith(`asset_${indexToRemove}_`)) {
                    delete updatedErrors[key];
                }
            });

            const shiftedErrors = {};
            Object.keys(updatedErrors).forEach((key) => {
                const match = key.match(/^asset_(\d+)_/);
                if (match) {
                    const originalIndex = parseInt(match[1], 10);
                    if (originalIndex > indexToRemove) {
                        const newKey = key.replace(`asset_${originalIndex}_`, `asset_${originalIndex - 1}_`);
                        shiftedErrors[newKey] = updatedErrors[key];
                    } else {
                        shiftedErrors[key] = updatedErrors[key];
                    }
                } else {
                    shiftedErrors[key] = updatedErrors[key];
                }
            });

            return shiftedErrors;
        });

        if (disableButton) {
            setDisableButton(false);
        }
    };

    const addAsset = () => {
        setAssetEntries(prev => ([
            ...prev,
            {
                itemName: '',
                serialNumber: '',
                cost: '',
                particulars: '',
                coordinates: '',
            }
        ]));
    };

    const triggerDelete = () => {
        setShowConfirmDialog(true);
    }

    const handleDeleteInventoryItem = () => {
        setShowConfirmDialog(false);
        dispatch(deleteInventoryItem(id)).then((resp) => {
            updateResponse(resp, resp?.data?.message);
        })
    }

    if (showLoader) {
        return <Grid container>
            <Grid style={{ display: 'flex', justifyContent: 'center', width: '100vw', height: '500px', alignItems: 'center' }}>
                <CircularProgress />
            </Grid>
        </Grid>
    }

    if (id && itemNotFound && !showLoader) {
        return <Grid container >
            <Grid item md={12} className='w3-padding' style={{ display: 'flex', justifyContent: 'center' }}>
                <h5 className='w3-text-grey'>
                    No Data Found
                </h5>
            </Grid>
        </Grid>
    }

    return (
        <Paper elevation={1} style={{ margin: '16px' }}>
            <Grid container className='w3-padding-small'>
                <Grid container >
                    <Grid item md={12} className='w3-padding' style={{ display: 'flex' }}>
                            <IconButton
                                onClick={() =>inventoryQueryParams ?
                                    navigate(`/inventory-management/${tabId}?${inventoryQueryParams.toString()}`) :
                                    navigate(`/inventory-management/${tabId}`)
                                }
                                color="inherit">
                                <ArrowBackIosIcon fontSize='small' />
                            </IconButton>
                        <h6>{id ? 'Edit Item' : 'Add Item'}</h6>
                    </Grid>
                    <Grid container item md={12} style={{ display: 'flex', flexDirection: 'row', }}>
                        <Grid container item md={12} className='w3-padding' style={{ alignContent: 'flex-start' }}>
                            <Grid item xs={12}>
                                {assetEntries.map((asset, index) => (
                                    <>
                                        <Paper
                                            key={index}
                                            elevation={4}
                                            className="w3-padding w3-margin-bottom"
                                        >
                                            <Grid item md={12} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                                <h6>{!id ? `Item #${index + 1}` : ''}</h6>
                                                {assetEntries?.length > 1 && (
                                                    <Tooltip title="Remove" className='w3-right' style={{ position: 'absolute', top: '8px', right: '6px' }}>
                                                        <Grid item style={{ display: 'flex', justifyContent: "flex-end" }} className="cursor-pointer font-size-12 error-text" onClick={() => handleRemoveAssetEntry(index)}>
                                                            <CloseIcon name="remove" />
                                                        </Grid>
                                                    </Tooltip>
                                                )}
                                            </Grid>
                                            <Grid item md={12} style={{ display: 'flex', flexDirection: 'row', }} className='w3-margin-bottom'>
                                                <Autocomplete
                                                    fullWidth
                                                    options={assetNames}
                                                    filterSelectedOptions
                                                    getOptionLabel={(option) => `${option?.asset_name}`}
                                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                    renderInput={(params) => <TextField sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#008080',
                                                            },
                                                        },
                                                        '& .MuiInputLabel-root.Mui-focused': {
                                                            color: '#008080',
                                                        },
                                                    }}  {...params} label="Item Name*" />}
                                                    onChange={(e, value) => handleAssetChange(index, 'itemName', value?.id || '')}
                                                    value={assetNames.find((item) => item.id === asset.itemName) || null}
                                                >
                                                </Autocomplete>
                                            </Grid>
                                            {errors[`asset_${index}_itemName`] && (
                                                <div
                                                    style={{ marginTop: '-12px' }}
                                                    className="error-text w3-margin-bottom"
                                                >
                                                    {errors[`asset_${index}_itemName`]}
                                                </div>
                                            )}
                                            <Grid item md={12} style={{ display: 'flex', flexDirection: 'row', }} className='w3-margin-bottom'>
                                                <TextField
                                                    fullWidth
                                                    required
                                                    label="Serial Number"
                                                    value={asset.serialNumber}
                                                    onChange={(e) => handleAssetChange(index, 'serialNumber', e.target.value)}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#008080',
                                                            },
                                                        },
                                                        '& .MuiInputLabel-root.Mui-focused': {
                                                            color: '#008080',
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            {errors[`asset_${index}_serialNumber`] && (
                                                <div
                                                    style={{ marginTop: '-12px' }}
                                                    className="error-text w3-margin-bottom"
                                                >
                                                    {errors[`asset_${index}_serialNumber`]}
                                                </div>
                                            )}
                                            <Grid container spacing={2}>
                                                <Grid item md={4} style={{ display: 'flex', flexDirection: 'row', }} >
                                                    <TextField
                                                        fullWidth
                                                        required
                                                        label="Asset Cost(₹)"
                                                        value={asset.cost}
                                                        onChange={(e) => handleAssetChange(index, 'cost', e.target.value)}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: '#008080',
                                                                },
                                                            },
                                                            '& .MuiInputLabel-root.Mui-focused': {
                                                                color: '#008080',
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item md={4} style={{ display: 'flex', flexDirection: 'row', }} >
                                                    <TextField
                                                        fullWidth
                                                        label="Bill Number"
                                                        value={asset.billNumber || ''}
                                                        onChange={(e) => handleAssetChange(index, 'billNumber', e.target.value)}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: '#008080',
                                                                },
                                                            },
                                                            '& .MuiInputLabel-root.Mui-focused': {
                                                                color: '#008080',
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item md={4} style={{ display: 'flex', flexDirection: 'row', }} >
                                                    <TextField
                                                        fullWidth
                                                        label="Vendor Name"
                                                        value={asset.vendorName || ''}
                                                        onChange={(e) => handleAssetChange(index, 'vendorName', e.target.value)}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: '#008080',
                                                                },
                                                            },
                                                            '& .MuiInputLabel-root.Mui-focused': {
                                                                color: '#008080',
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid container spacing={2}>
                                                <Grid item md={4}>
                                                    {errors[`asset_${index}_cost`] && (
                                                        <div className="error-text">{errors[`asset_${index}_cost`]}</div>
                                                    )}
                                                </Grid>
                                            </Grid>
                                            <Grid container spacing={2}>
                                                <Grid item md={4} sx={{ mt: 2, mb: 2 }}>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                                                        <DatePicker
                                                            label="Date of Purchase*"
                                                            format="DD/MM/YYYY"
                                                            required
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true, error: false, sx: {
                                                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: '#008080'
                                                                        },
                                                                        '& .MuiInputLabel-root.Mui-focused': {
                                                                            color: '#008080'
                                                                        }
                                                                    }
                                                                }, day: {
                                                                    sx: {
                                                                        '&.Mui-selected': {
                                                                            backgroundColor: '#008080 !important',
                                                                            '&:hover': {
                                                                                backgroundColor: '#006666 !important'
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                            name="purchaseDate"
                                                            value={asset.purchaseDate ? dayjs(asset.purchaseDate) : null}
                                                            onChange={(newValue) => handleAssetChange(index, 'purchaseDate', newValue)}
                                                            maxDate={dayjs()}
                                                        />
                                                    </LocalizationProvider>
                                                    {errors[`asset_${index}_purchaseDate`] && (
                                                        <div className='error-text'>{errors[`asset_${index}_purchaseDate`]}</div>
                                                    )}
                                                </Grid>
                                                <Grid item md={4} sx={{ mt: 2, mb: 2 }}>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                                                        <DatePicker
                                                            label="End of Warranty Date"
                                                            format="DD/MM/YYYY"
                                                            disabled={!asset.purchaseDate}
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true, error: false, sx: {
                                                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: '#008080'
                                                                        },
                                                                        '& .MuiInputLabel-root.Mui-focused': {
                                                                            color: '#008080'
                                                                        }
                                                                    }
                                                                }, day: {
                                                                    sx: {
                                                                        '&.Mui-selected': {
                                                                            backgroundColor: '#008080 !important',
                                                                            '&:hover': {
                                                                                backgroundColor: '#006666 !important'
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                            name="endOfWarrantyDate"
                                                            value={asset.endOfWarrantyDate ? dayjs(asset.endOfWarrantyDate) : null}
                                                            onChange={(newValue) => handleAssetChange(index, 'endOfWarrantyDate', newValue)}
                                                            minDate={dayjs(asset.purchaseDate)}
                                                        />
                                                    </LocalizationProvider>
                                                    {errors[`asset_${index}_endOfWarrantyDate`] && (
                                                        <div className='error-text w3-margin-bottom'>
                                                            {errors[`asset_${index}_endOfWarrantyDate`]}
                                                        </div>
                                                    )}
                                                </Grid>
                                            </Grid>
                                            <Grid container spacing={2}>
                                                <Grid item md={12} sx={{ mt: 2, mb: 2 }}>
                                                    <TextField
                                                        fullWidth
                                                        value={asset.coordinates}
                                                        label="Geo Tagging"
                                                        placeholder='12.9716, 77.5946'
                                                        name="coordinates"
                                                        onChange={(e) => handleAssetChange(index, 'coordinates', e.target.value)}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton onClick={() => getCurrentLocation(index)}>
                                                                        <MyLocationIcon />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: '#008080',
                                                                },
                                                            },
                                                            '& .MuiInputLabel-root.Mui-focused': {
                                                                color: '#008080',
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                            {errors[`asset_${index}_coordinates`] && (
                                                <div style={{ marginTop: '-12px' }} className='error-text w3-margin-bottom'>
                                                    {errors[`asset_${index}_coordinates`]}
                                                </div>
                                            )}
                                            <Grid item md={12} style={{ display: 'flex', flexDirection: 'row', }} className='w3-margin-bottom'>
                                                <TextField
                                                    fullWidth
                                                    required
                                                    multiline
                                                    rows={4}
                                                    label="Particulars"
                                                    value={asset.particulars}
                                                    onChange={(e) => handleAssetChange(index, 'particulars', e.target.value)}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#008080',
                                                            },
                                                        },
                                                        '& .MuiInputLabel-root.Mui-focused': {
                                                            color: '#008080',
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            {errors[`asset_${index}_particulars`] && (
                                                <div style={{ marginTop: '-12px' }} className='error-text w3-margin-bottom'>
                                                    {errors[`asset_${index}_particulars`]}
                                                </div>
                                            )}
                                        </Paper>
                                    </>))}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item md={12} style={{ display: 'flex', flexDirection: 'row' }}>
                        <Grid item md={6} style={{ display: 'flex', justifyContent: 'flex-start', paddingLeft: '16px' }} className='w3-margin-bottom'>
                            {!id && (<Button
                                onClick={addAsset}
                                variant="outlined"
                                style={{ fontWeight: 'bold', borderColor: '#008080', color: '#008080' }}>
                                + Add Asset
                            </Button>)}
                        </Grid>
                        <Grid item md={6} style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '16px' }} className='w3-margin-bottom'>
                            {id && (<Button
                                variant="outlined"
                                color="error"
                                onClick={() => triggerDelete()}
                            >
                                Delete
                            </Button>)}

                            <Button
                                variant="outlined"
                                style={{ marginLeft: '10px', borderColor: '#008080', color: '#008080' }}
                                onClick={() => {
                                    inventoryQueryParams ?
                                        navigate(`/inventory-management/${tabId}?${inventoryQueryParams.toString()}`) :
                                        navigate(`/inventory-management/${tabId}`)
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                style={{ marginLeft: '10px', backgroundColor: '#008080', color: 'white' }}
                                disabled={disableButton}
                            >
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                {alertMessage ?
                    (
                        <AlertDialog
                            handleClose={handleCloseDialog}
                            message={alertMessage}
                            type="fail"
                            handleDialogClose={() => setAlertMessage(null)}
                        />
                    )
                    : null
                }
                {showConfirmDialog
                    ? (
                        <ConfirmDialog
                            handleCancel={() => {
                                setShowConfirmDialog(false)
                            }}
                            handleOk={handleDeleteInventoryItem}
                            message={'Are you sure to delete this ?'}
                        />
                    ) : null}

                {clickSubmitButton && <Loader open={clickSubmitButton} msg="Please wait...!" />}
            </Grid>
            <Snackbar
                open={!!geoError}
                autoHideDuration={5000}
                onClose={() => setGeoError('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setGeoError('')} severity="error" sx={{ width: '100%' }}>
                    {geoError}
                </Alert>
            </Snackbar>
        </Paper>
    )
}